import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender, Header } from "@tanstack/react-table";
import { CSSProperties, ReactNode, useMemo } from "react";

interface TableThProps<TData, TValue> {
  header: Header<TData, TValue>;
  children?: ReactNode;
}

export const TableTh = <TData, TValue>({ header, children }: TableThProps<TData, TValue>) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
  });

  // const size = header.column.getSize();
  const isPinned = header.column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === "left" && header.column.getIsLastColumn("left");
  const isFirstRightPinnedColumn = isPinned === "right" && header.column.getIsFirstColumn("right");

  const style: CSSProperties = useMemo(
    () => ({
      boxShadow: isLastLeftPinnedColumn
        ? "-4px 0 4px -4px  inset"
        : isFirstRightPinnedColumn
          ? "4px 0 4px -4px  inset"
          : undefined,
      left: isPinned === "left" ? `${header.column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${header.column.getAfter("right")}px` : undefined,
      opacity: isDragging || isPinned ? 0.9 : 1,
      position: isPinned ? "sticky" : "relative",
      transform: CSS.Translate.toString(transform),
      transition: "width 0.2s linear, transform 0.2s linear",
      whiteSpace: "nowrap",
      width: `calc(var(--header-${header?.id}-size) * 1px)`,
      // width: size,
      zIndex: isDragging || isPinned ? 3 : 2,
    }),
    [
      isPinned,
      isDragging,
      transform,
      header.column,
      header?.id,
      isLastLeftPinnedColumn,
      isFirstRightPinnedColumn,
    ]
  );

  return (
    <th
      ref={setNodeRef}
      style={style}
      colSpan={header.colSpan}
      className={`relative h-12 px-4 text-center align-middle font-medium text-muted-foreground bg-background`}
    >
      <div className="flex gap-x-1 items-center justify-center">
        <span {...attributes} {...listeners}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
        {children}
      </div>
    </th>
  );
};
